<script setup>
import TypingIcons from '@/components/Typing.vue'
import SourceModalContent from '@/components/SourceModalContent.vue'
import { ref, defineProps, computed } from 'vue'
import { renderMarkdown } from '@/converter'
import ModalSource from '@/components/modals/ModalSource.vue'
import { useWidget } from '@/widget'
import ShowIcon from '@/assets/icons/show.svg'
import AssistantBasicIcon from '@/assets/icons/assistant.svg'
import FixIcon from '@/assets/icons/fix.svg'
import ChartIcon from '@/assets/icons/chart.svg'
import TransitionFade from '@/components/TransitionFade.vue'
import { useMessages } from '@/message'
import { useTranslations } from '@/translation'
import AdaptiveCard from './AdaptiveCard.vue'
import ChatMessageOptions from './ChatMessageOptions.vue'
import { mapToCorrectProduct } from '@/helpers'
const { currentBotMessageId } = useMessages()
const { config, getOption, sendPostMessage } = useWidget()
const assistantImage = computed(() => getOption('assistantImage'))
const emit = defineEmits(['send'])
const showSourceModal = ref(false)
const isLatestMessage = computed(() => currentBotMessageId.value === props.message.id)
const props = defineProps({
  index: Number,
  message: {
    sender: String,
    content: String,
    bot: Boolean,
    time: Date
  }
})
const { t } = useTranslations()

const convertMsg = computed(() => renderMarkdown(props.message.content) || '')
const isBot = computed(() => props.message.bot)
const handleFixMessage = () => {
  sendPostMessage({ ...props.message }, 'fixMessage')
}
const handleSource = () => {
  showSourceModal.value = !showSourceModal.value
  sendPostMessage({ ...props.message }, 'showSource')
}
const messageSent = computed(() => props.message.status === 'sent')
const tooltip = computed(() => t('tooltip.confidenceScore'))
const productSource = computed(() => {
  const products = props.message.source.filter(
    (source) =>
      source.context === 'product' ||
      source.source_type === 'product' ||
      source.document_context === 'product'
  )
  return products
})
const compactProducts = computed(() => {
  return productSource.value.map((product) => mapToCorrectProduct(product))
})
const hasProductSource = computed(
  () =>
    props.message &&
    props.message.source &&
    props.message?.source.length > 0 &&
    compactProducts.value.length > 0
)
</script>

<template>
  <div
    class="flex flex-col px-5 max-w-[100%]"
    :class="{
      'items-start': isBot,
      'items-end': !isBot
    }"
    role="group"
    :aria-label="
      isBot
        ? `${t('aria.botMessageFrom', { sender: message.sender })}`
        : `${t('aria.userMessageFrom', { sender: message.sender })}`
    "
    tabindex="0"
  >
    <div class="flex gap-1 items-center mb-2">
      <div v-if="isBot" class="relative flex shrink-0 overflow-hidden rounded-full w-6 h-6">
        <div
          v-if="isBot && !assistantImage"
          class="rounded-full w-full h-full bg-app text-primary flex items-center justify-center"
        >
          <AssistantBasicIcon v-if="!assistantImage" aria-label="Bot avatar" />
        </div>
        <div class="w-full h-full" v-else>
          <img
            class="w-full h-full rounded-full object-cover"
            :src="assistantImage"
            alt="Bot avatar"
          />
        </div>
      </div>
      <div class="block font-bold text-primary text-sm relative w-full">
        {{ message.sender }}
      </div>
    </div>

    <div
      class="flex flex-col p-4 max-w-[100%] rounded-bubble-radius has-tooltip relative"
      :class="{
        'bg-chat-message-background  rounded-bubble-radius  !rounded-tl-none': isBot,
        'bg-user-message-background  rounded-bubble-radius !rounded-tr-none': !isBot
      }"
      :aria-live="isBot ? 'polite' : null"
    >
      <div
        v-if="message.content"
        class="prose text-sm"
        v-html="convertMsg"
        :class="{
          'prose-user': !isBot,
          'prose-bot': isBot
        }"
      />

      <TypingIcons v-else />

      <template v-if="hasProductSource">
        <div class="flex flex-col gap-5 border-t border-t-appBorder mt-3">
          <AdaptiveCard template="product" v-for="p in compactProducts" :product="p" :key="p.url" />
        </div>
      </template>

      <template v-if="config.adminMode && messageSent && index > 0">
        <template v-if="isBot">
          <div
            class="pt-4 mt-4 border-t border-app/80 flex flex-wrap gap-3 items-center justify-between"
          >
            <div
              v-if="message.confidenceScore && message.confidenceScore > 0"
              v-tooltip="tooltip"
              class="text-chat-message-background rounded-[10px] bg-chat-message-color text-[10px] text-nowrap p-1 px-3 flex items-center gap-1"
              aria-label="{{ t('aria.confidenceScore', { score: message.confidenceScore }) }}"
            >
              <ChartIcon />
              <div class="font-bold">
                {{ message.confidenceScore }}
              </div>
            </div>
            <div class="ml-auto flex items-center flex-wrap gap-3">
              <button
                v-if="config.fixMessage"
                @click="handleFixMessage"
                class="text-chat-message-background rounded-[10px] bg-chat-message-color text-[10px] text-nowrap p-1 px-3 flex items-center gap-1"
                type="button"
                aria-label="{{ t('aria.fixMessage') }}"
              >
                <FixIcon />
                {{ t('buttons.fixMessage') }}
              </button>
              <div v-if="config.showSource && message.source.length" class="flex justify-end">
                <button
                  @click="handleSource"
                  class="text-chat-message-background rounded-[10px] text-[10px] bg-chat-message-color text-nowrap p-1 px-3 flex items-center gap-1"
                  type="button"
                  aria-label="{{ t('aria.showSource') }}"
                >
                  <ShowIcon />
                  {{ t('buttons.showSource') }}
                </button>
                <ModalSource v-model="showSourceModal">
                  <SourceModalContent :message="message" />
                </ModalSource>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
    <ChatMessageOptions :message="message" v-if="isLatestMessage && messageSent">
    </ChatMessageOptions>
    <TransitionFade>
      <div
        class="flex gap-2 w-full flex-wrap mt-3"
        v-if="
          message.suggestedQuestions &&
          message.suggestedQuestions?.length > 0 &&
          (config.preview ? true : isLatestMessage)
        "
      >
        <button
          v-for="question in message.suggestedQuestions"
          :key="question"
          @click="emit('send', question)"
          type="button"
          class="rounded-suggestion-radius bg-suggestion px-3 py-2 text-xs font-semibold text-suggestionText shadow-sm text-left disabled:cursor-not-allowed disabled:opacity-50"
          :aria-label="`${t('aria.suggestedQuestion')} ${question}`"
        >
          {{ question }}
        </button>
      </div>
    </TransitionFade>
  </div>
</template>
